import React from 'react';
import {
    Box,
    Grid,
    InputLabel,
    Slider,
    styled,
    TextField,
    Typography
} from '@mui/material';
import { debounce } from 'lodash';

// import { validateNumberRange } from '../validations/validations';

type PropsType = {
    name: string,
    label: string,
    lowValue: number,
    highValue: number,
    minValue: number,
    maxValue: number,
    step?: number,
    required?: boolean,
    showInputFields?: boolean,
    /** Tighter layout: label + range summary (or min/max inputs with showInputFields) around a small slider */
    compact?: boolean,
    // helperText?: string,
    onChange: (values: [number | null, number | null]) => void
};

const StyledSlider = styled(Slider)({
    '& .MuiSlider-valueLabel': {
        top: 55,
        '&:before': { top: -7 }
    },
});
export default function RangeField(props: PropsType) {
    const {
        name,
        label,
        lowValue,
        highValue,
        minValue,
        maxValue,
        step,
        onChange,
        required,
        showInputFields = false,
        compact = false
        // ...baseProps
    } = props;

    const [value, setValue] = React.useState<number[]>([Math.max(lowValue, minValue), Math.min(highValue, maxValue)]);

    React.useEffect(() => {
        setValue([Math.max(lowValue, minValue), Math.min(highValue, maxValue)]);
    }, [lowValue, highValue, minValue, maxValue]);

    const handleChange = React.useCallback((event, val) => {
        setValue(val);
    }, []);

    const commitChange = React.useCallback((event, val) => {
        onChange([val[0] === minValue ? null : val[0], val[1] === maxValue ? null : val[1]]);
    }, [maxValue, minValue, onChange]);

    const stepValue = React.useMemo(() => (
        step || Math.round((maxValue - minValue) / Math.min(100, maxValue - minValue))
    ), [minValue, maxValue, step]);

    const displayLabelState = React.useMemo(() => (
        value[0] === minValue && value[1] === maxValue
            ? 'off'
            : 'on'
    ), [value, maxValue, minValue]);

    const getLabel = React.useCallback((val) => {
        if (val === minValue) {
            return 'Min';
        }
        if (val === maxValue) {
            return 'Max';
        }
        return val;
    }, [minValue, maxValue]);

    const debouncedOnChange = React.useMemo(() => debounce((newMin: number | null, newMax: number | null) => {
        onChange([newMin, newMax]);
    }, 100), [onChange]);

    const handleInputChange = (index: 0 | 1, inputValue: string) => {
        const numericValue = Number(inputValue);
        const newValue = [...value] as [number, number];

        if (index === 0) {
            newValue[0] = Math.min(numericValue, newValue[1]);
        } else {
            newValue[1] = Math.max(numericValue, newValue[0]);
        }

        newValue[0] = Math.max(minValue, newValue[0]);
        newValue[1] = Math.min(maxValue, newValue[1]);

        setValue(newValue);
        debouncedOnChange(newValue[0] === minValue ? null : newValue[0], newValue[1] === maxValue ? null : newValue[1]);
    };

    // const validations = [];
    // if (typeof minValue === 'number' || typeof maxValue === 'number') {
    //     validations.push(validateNumberRange({ value: baseProps.value, minValue, maxValue }));
    // }

    if (compact) {
        const slider = (
            <Slider
                name={name}
                getAriaLabel={() => label}
                getAriaValueText={(v) => `${v}`}
                size="small"
                value={value}
                max={maxValue}
                min={minValue}
                step={stepValue}
                onChange={handleChange}
                onChangeCommitted={commitChange}
                valueLabelDisplay="off"
                sx={{ display: 'block', py: showInputFields ? 1.5 : 0.75 }}
            />
        );

        if (!showInputFields) {
            const unit = /\[([^\]]+)\]/.exec(label)?.[1];
            const fmt = (n: number) => n.toLocaleString('sv-SE');
            let summary = '';
            if (value[0] !== minValue && value[1] !== maxValue) {
                summary = `${fmt(value[0])}–${fmt(value[1])}`;
            } else if (value[0] !== minValue) {
                summary = `Minst ${fmt(value[0])}`;
            } else if (value[1] !== maxValue) {
                summary = `Högst ${fmt(value[1])}`;
            }
            return (
                <Box>
                    <Box sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1
                    }}
                    >
                        <InputLabel sx={{ mb: 0 }}>
                            {`${label.replace(/\s*\[[^\]]+\]\s*$/, '')}${required ? ' *' : ''}`}
                        </InputLabel>
                        <Typography
                            variant="caption"
                            color={summary ? 'text.primary' : 'text.secondary'}
                            sx={{ fontWeight: summary ? 600 : 400, whiteSpace: 'nowrap' }}
                        >
                            {summary && unit ? `${summary} ${unit}` : summary || 'Alla'}
                        </Typography>
                    </Box>
                    <Box sx={{ px: 1.5 }}>{slider}</Box>
                </Box>
            );
        }

        // Container query: [min] slider [max] on one row when the field is wide enough, else inputs below
        const wide = '@container (min-width: 300px)';
        const input = (index: 0 | 1) => (
            <TextField
                type="number"
                size="small"
                placeholder={index === 0 ? 'Min' : 'Max'}
                value={value[index] === (index === 0 ? minValue : maxValue) ? '' : value[index]}
                onChange={(e) => handleInputChange(index, e.target.value)}
                inputProps={{
                    step: stepValue,
                    min: index === 0 ? minValue : value[0],
                    max: index === 0 ? value[1] : maxValue,
                    'aria-label': `${label}, ${index === 0 ? 'min' : 'max'}`
                }}
                sx={{
                    width: '100%',
                    gridColumn: index + 1,
                    gridRow: 2,
                    '& input': { textAlign: 'center' },
                    '& input[type=number]': { MozAppearance: 'textfield' },
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0
                    },
                    [wide]: { width: 80, gridColumn: index === 0 ? 1 : 3, gridRow: 1 }
                }}
            />
        );
        return (
            <Box sx={{ containerType: 'inline-size' }}>
                <InputLabel sx={{ mb: 0.5 }}>
                    {`${label}${required ? ' *' : ''}`}
                </InputLabel>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                        columnGap: 2,
                        alignItems: 'center',
                        [wide]: { gridTemplateColumns: 'auto minmax(0, 1fr) auto', columnGap: 1.5 }
                    }}
                >
                    <Box sx={{
                        gridColumn: '1 / -1', gridRow: 1, px: 1.5, [wide]: { gridColumn: 2 }
                    }}
                    >
                        {slider}
                    </Box>
                    {input(0)}
                    {input(1)}
                </Box>
            </Box>
        );
    }

    return (
        <>
            <InputLabel>
                {`${label}${required ? ' *' : ''}`}
            </InputLabel>
            <div style={{
                paddingBottom: '20px',
                paddingLeft: '30px',
                paddingRight: '30px'
            }}
            >
                <StyledSlider
                    name={name}
                    // getAriaLabel={getLabel}
                    value={value}
                    max={maxValue}
                    min={minValue}
                    step={stepValue}
                    onChange={handleChange}
                    onChangeCommitted={commitChange}
                    // valueLabelDisplay="on"
                    valueLabelFormat={getLabel}
                    valueLabelDisplay={showInputFields ? 'off' : displayLabelState}
                // getAriaValueText={getLabel}
                />
                {showInputFields && (
                    <Grid container spacing={2} justifyContent="space-between">
                        <Grid item>
                            <TextField
                                label="Min"
                                type="number"
                                size="small"
                                value={value[0] === minValue ? '' : value[0]}
                                onChange={(e) => handleInputChange(0, e.target.value)}
                                // onBlur={commitChange}
                                inputProps={{
                                    step: stepValue,
                                    min: minValue,
                                    max: value[1]
                                }}
                                style={{ width: 100 }}
                            />
                        </Grid>
                        <Grid item>
                            <TextField
                                label="Max"
                                type="number"
                                size="small"
                                value={value[1] === maxValue ? '' : value[1]}
                                onChange={(e) => handleInputChange(1, e.target.value)}
                                // onBlur={commitChange}
                                inputProps={{
                                    step: stepValue,
                                    min: value[0],
                                    max: maxValue
                                }}
                                style={{ width: 100 }}
                            />
                        </Grid>
                    </Grid>                  
                )}

            </div>
        </>
    );
}
